import React, { useEffect, useState } from 'react';
import { View, ScrollView, ActivityIndicator, Clipboard, Linking, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ErrorState } from '@/components/ui/error-state';
import { ProofUploadModal } from '@/components/charges/proof-upload-modal';
import { WelcomeModal } from '@/components/checkout/welcome-modal';
import { AutomaticCheckout } from '@/components/checkout/automatic-checkout';
import { useToastStore } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { Ionicons } from '@expo/vector-icons';
import api from '@/services/api';
import { Logo } from '@/components/logo';
import { cleanCheckoutChargeId } from '@/utils/clean-checkout-id';
import { trackInitiateCheckout, trackPurchase } from '@/lib/meta-pixel';
import { trackCheckoutEvent } from '@/lib/checkout-analytics';
import type { CheckoutPaymentMethod } from '@/types/checkout';
import { getApiErrorMessage } from '@/utils/api-error';

interface PublicChargeDetails {
  id: string;
  amount: number;
  dueDate: string;
  status: string;
  pixQrCode?: string;
  pixCopyPaste?: string;
  nossoNumero?: string;
  boletoDigitableLine?: string;
  serviceName?: string;
  providerName?: string;
  isManual?: boolean;
  // Campos extras para cobranças manuais
  description?: string;
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  paymentInstructions?: string;
  paymentLink?: string;
  proofOfPaymentUrl?: string;
  // Checkout automático (T2.2/T5.1) — `mode` sempre vem; os demais só no automático.
  mode?: 'MANUAL' | 'AUTOMATIC';
  paymentMethods?: CheckoutPaymentMethod[];
  stripeAccount?: string | null;
  publishableKey?: string | null;
}

/** Na web, o parâmetro pode vir do pathname; normaliza para string. */
function getRawChargeIdFromParams(params: { chargeId?: string | string[] }): string {
  const v = params?.chargeId;
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && v.length) return String(v[0]);
  return '';
}

/** Extrai o segmento de checkout do pathname (fallback para web). */
function getRawChargeIdFromPathname(): string {
  if (typeof window === 'undefined') return '';
  const pathname = window.location.pathname;
  const match = pathname.match(/\/checkout\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/** Enviar comprovante / informar pagamento sem comprovante (modo manual). */
function ReportPaymentSection({
  onUploadPress,
  onReportPress,
}: {
  onUploadPress: () => void;
  onReportPress: () => void;
}) {
  return (
    <View className="border-t border-border pt-4">
      <ThemedText className="mb-4 text-center text-sm text-foreground/60">
        Já realizou o pagamento? Envie o comprovante para agilizar a confirmação.
      </ThemedText>
      <Button variant="default" onPress={onUploadPress}>
        <View className="flex-row items-center gap-2">
          <Ionicons name="cloud-upload" size={18} color="white" />
          <ThemedText className="font-semibold text-white">Enviar comprovante</ThemedText>
        </View>
      </Button>

      <Button variant="ghost" className="mt-2" onPress={onReportPress}>
        <ThemedText className="text-foreground/60 underline">
          Informar pagamento sem comprovante
        </ThemedText>
      </Button>
    </View>
  );
}

export default function PublicCheckoutScreen() {
  const params = useLocalSearchParams<{ chargeId?: string | string[] }>();
  const rawChargeId = getRawChargeIdFromParams(params) || getRawChargeIdFromPathname();
  const chargeId = cleanCheckoutChargeId(rawChargeId);
  const router = useRouter();
  const { addToast } = useToastStore();
  const { isAuthenticated } = useAuthStore();

  const [charge, setCharge] = useState<PublicChargeDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [welcomeModalShown, setWelcomeModalShown] = useState(false);
  const initCheckoutFiredRef = React.useRef(false);
  const purchaseFiredRef = React.useRef(false);
  const pageViewFiredRef = React.useRef(false);

  // Analytics: checkout page view (uma vez por mount)
  useEffect(() => {
    if (chargeId && !pageViewFiredRef.current && Platform.OS === 'web') {
      pageViewFiredRef.current = true;
      trackCheckoutEvent({ event: 'checkout_page_view', chargeId });
    }
  }, [chargeId]);

  // InitiateCheckout - Meta: audiência de quem chegou no checkout
  useEffect(() => {
    if (!charge || isLoading || initCheckoutFiredRef.current || Platform.OS !== 'web') return;
    initCheckoutFiredRef.current = true;
    trackInitiateCheckout({
      value: charge.amount / 100,
      currency: 'BRL',
      contentIds: [charge.id],
    });
  }, [charge, isLoading]);

  // Se a URL veio com placeholder do WhatsApp (ex.: /checkout/%7B%7B1%7D%7Duuid), redireciona para a URL limpa
  useEffect(() => {
    if (!chargeId || rawChargeId === chargeId) return;
    const cleanPath = `/checkout/${chargeId}`;
    router.replace(cleanPath as never);
    // Na web, garantir que a barra de endereço seja atualizada (router.replace pode não fazer isso)
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const pathname = window.location.pathname;
      const newPath = pathname.replace(/\/checkout\/[^/]*/, cleanPath);
      if (newPath !== pathname) {
        window.history.replaceState(null, '', newPath);
      }
    }
  }, [rawChargeId, chargeId, router]);

  // Polling para verificar status do pagamento
  useEffect(() => {
    if (!charge || charge.status === 'PAID') return;

    const pollStatus = async () => {
      try {
        const response = await api.get(`/charge/${chargeId}/public`);
        const updatedCharge = response.data;

        if (updatedCharge.status === 'PAID') {
          setCharge(updatedCharge);

          if (Platform.OS === 'web' && !purchaseFiredRef.current) {
            purchaseFiredRef.current = true;
            trackPurchase({
              value: (updatedCharge.amount ?? 0) / 100,
              currency: 'BRL',
              contentIds: [updatedCharge.id],
              orderId: updatedCharge.id,
            });
          }

          setIsPolling(false);
          addToast('Pagamento confirmado com sucesso!', 'success');
        }
      } catch (err) {
        console.error('Erro ao sincronizar status:', err);
      }
    };

    // Iniciar polling a cada 5 segundos
    const interval = setInterval(pollStatus, 5000);
    setIsPolling(true);

    return () => {
      clearInterval(interval);
      setIsPolling(false);
    };
  }, [charge, chargeId, addToast]);

  useEffect(() => {
    const fetchChargeDetails = async () => {
      if (!chargeId) {
        setError('ID da cobrança não encontrado');
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/charge/${chargeId}/public`);
        const chargeData = response.data;

        console.log('Charge data received:', {
          hasPixQrCode: !!chargeData.pixQrCode,
          hasPixCopyPaste: !!chargeData.pixCopyPaste,
          hasNossoNumero: !!chargeData.nossoNumero,
        });

        setCharge(chargeData);

        if (Platform.OS === 'web') {
          trackCheckoutEvent({ event: 'checkout_load_success', chargeId });
        }

        // Purchase - se já veio pago (ex: refresh após pagar)
        if (Platform.OS === 'web' && chargeData.status === 'PAID' && !purchaseFiredRef.current) {
          purchaseFiredRef.current = true;
          trackPurchase({
            value: (chargeData.amount ?? 0) / 100,
            currency: 'BRL',
            contentIds: [chargeData.id],
            orderId: chargeData.id,
          });
        }

        // Don't auto-select - let user choose
      } catch (err: any) {
        console.error('Erro ao carregar cobrança:', err);
        if (Platform.OS === 'web') {
          trackCheckoutEvent({
            event: 'checkout_load_error',
            chargeId,
            errorMessage: err?.response?.data?.message || err?.message || 'Unknown error',
            httpStatus: err?.response?.status,
          });
        }
        setError(getApiErrorMessage(err, 'Não foi possível carregar os dados da cobrança'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchChargeDetails();
  }, [chargeId]);

  // Mostrar modal de boas-vindas para usuários não logados
  useEffect(() => {
    if (!isLoading && charge && !isAuthenticated && !welcomeModalShown) {
      // Pequeno delay para permitir que a tela seja renderizada primeiro
      const timer = setTimeout(() => {
        setWelcomeModalOpen(true);
        setWelcomeModalShown(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, charge, isAuthenticated, welcomeModalShown]);

  const handleCopyPixCode = async () => {
    if (!charge?.pixCopyPaste) return;

    await Clipboard.setString(charge.pixCopyPaste);
    addToast('Código Pix copiado!', 'success');
  };

  const handleReportPayment = async () => {
    if (!charge) return;

    try {
      await api.post(`/charge/${charge.id}/report-payment`);
      addToast('Pagamento informado com sucesso!', 'success');

      // Reload charge data to show updated status
      const response = await api.get(`/charge/${chargeId}/public`);
      setCharge(response.data);
    } catch {
      addToast('Erro ao informar pagamento', 'error');
    }
  };

  if (isLoading) {
    return (
      <View className="flex-1 bg-background">
        <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 20 }}>
          <View className="items-center justify-center py-20">
            <ActivityIndicator size="large" />
            <ThemedText className="mt-4 text-foreground/60">Carregando cobrança...</ThemedText>
          </View>
        </ScrollView>
      </View>
    );
  }

  if (error || !charge) {
    return (
      <View className="flex-1 bg-background">
        <ErrorState
          title="Erro ao carregar cobrança"
          message={error || 'Cobrança não encontrada'}
          onRetry={() => router.back()}
        />
      </View>
    );
  }

  const isPaid = charge.status === 'PAID';

  return (
    <View className="flex-1 bg-background">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1"
        contentContainerStyle={{ padding: 20, paddingBottom: 80 }}
      >
        <View className="mx-auto w-full max-w-md gap-6">
          {/* Header */}
          <View className="items-center gap-3">
            <Logo color="tint" />
            <ThemedText className="text-center font-bold text-2xl">
              {isPaid ? 'Pagamento Confirmado!' : 'Finalizar Pagamento'}
            </ThemedText>
            {charge.serviceName && (
              <ThemedText className="text-center text-base text-foreground/60">
                {charge.serviceName}
              </ThemedText>
            )}
            {charge.providerName && (
              <ThemedText className="text-center text-sm text-foreground/40">
                {charge.providerName}
              </ThemedText>
            )}
          </View>

          {/* Status do Pagamento */}
          {isPolling && !isPaid && (
            <View className="flex-row items-center gap-2 rounded-lg bg-blue-50 p-4">
              <Ionicons name="refresh" size={20} className="text-blue-600" />
              <ThemedText className="flex-1 text-sm text-blue-700">
                Aguardando confirmação do pagamento...
              </ThemedText>
            </View>
          )}

          {isPaid && (
            <View className="flex-row items-center gap-2 rounded-lg bg-green-50 p-4">
              <Ionicons name="checkmark-circle" size={20} className="text-green-600" />
              <ThemedText className="flex-1 text-sm text-green-700">
                Pagamento confirmado! Obrigado.
              </ThemedText>
            </View>
          )}

          {charge.status === 'PAYMENT_REPORTED' && (
            <View className="flex-row items-center gap-2 rounded-lg bg-premium-50 p-4">
              <Ionicons name="information-circle" size={20} className="text-premium" />
              <ThemedText className="flex-1 text-sm text-premium-700">
                Você já informou que realizou o pagamento. Aguarde a confirmação do prestador.
              </ThemedText>
            </View>
          )}

          {/* Detalhes da Cobrança */}
          <Card className="p-5">
            <View className="gap-4">
              <View className="flex-row items-center justify-between border-b border-border pb-4">
                <ThemedText className="font-medium text-base text-foreground/60">Valor</ThemedText>
                <ThemedText className="font-bold text-3xl text-primary">
                  {formatCurrency(charge.amount)}
                </ThemedText>
              </View>

              <View className="flex-row items-center justify-between">
                <ThemedText className="text-foreground/60">Vencimento</ThemedText>
                <ThemedText className="font-medium">
                  {formatDate(new Date(charge.dueDate))}
                </ThemedText>
              </View>

              <View className="flex-row items-center justify-between">
                <ThemedText className="text-foreground/60">Status</ThemedText>
                <View
                  className={`rounded-full px-3 py-1 ${isPaid ? 'bg-green-100' : charge.status === 'PAYMENT_REPORTED' ? 'bg-premium-100' : 'bg-orange-100'}`}
                >
                  <ThemedText
                    className={`font-medium text-sm ${isPaid ? 'text-green-700' : charge.status === 'PAYMENT_REPORTED' ? 'text-premium-700' : 'text-orange-700'}`}
                  >
                    {isPaid
                      ? 'Pago'
                      : charge.status === 'PAYMENT_REPORTED'
                        ? 'Pagamento Informado'
                        : 'Pendente'}
                  </ThemedText>
                </View>
              </View>
            </View>
          </Card>

          {/* Checkout automático (T5.1, plano 6.7): Payment Element (web) ou
              PaymentSheet (nativo) — nunca junto com o card manual abaixo. */}
          {!isPaid &&
            charge.status !== 'PAYMENT_REPORTED' &&
            charge.mode === 'AUTOMATIC' &&
            (charge.stripeAccount && charge.publishableKey ? (
              <Card className="p-5">
                <ThemedText className="mb-4 font-semibold text-lg">
                  Informações para Pagamento
                </ThemedText>
                <AutomaticCheckout
                  chargeId={charge.id}
                  stripeAccount={charge.stripeAccount}
                  publishableKey={charge.publishableKey}
                />
              </Card>
            ) : (
              // Cobrança marcada como automática mas sem subconta pronta
              // (ex.: conta desativada depois da cobrança nascer) — sem
              // Payment Element para montar.
              <View className="rounded-lg border border-border p-4">
                <ThemedText className="text-sm text-foreground/70">
                  O pagamento não está disponível agora. Fale com o prestador.
                </ThemedText>
              </View>
            ))}

          {/* Informações para pagamento (modo manual — nada muda, T5.1) */}
          {!isPaid && charge.status !== 'PAYMENT_REPORTED' && charge.mode !== 'AUTOMATIC' && (
            <Card className="p-5">
              <ThemedText className="mb-4 font-semibold text-lg">
                Informações para Pagamento
              </ThemedText>

              <View className="gap-6">
                {/* Descrição da cobrança */}
                {charge.description && (
                  <View className="rounded-lg bg-blue-50 p-3">
                    <ThemedText className="text-sm text-blue-800">{charge.description}</ThemedText>
                  </View>
                )}

                {/* Código PIX */}
                {charge.pixCopyPaste && (
                  <View className="gap-3">
                    <ThemedText className="font-medium text-sm">Pix Copia e Cola</ThemedText>
                    <View className="rounded-lg bg-muted p-3">
                      <ThemedText className="font-mono text-xs" numberOfLines={3}>
                        {charge.pixCopyPaste}
                      </ThemedText>
                    </View>
                    <Button variant="outline" onPress={handleCopyPixCode}>
                      <View className="flex-row items-center gap-2">
                        <Ionicons className="text-foreground" name="copy" size={18} />
                        <ThemedText>Copiar Código Pix</ThemedText>
                      </View>
                    </Button>
                  </View>
                )}

                {/* Link de Pagamento */}
                {charge.paymentLink && (
                  <View className="gap-2">
                    <ThemedText className="font-medium text-sm">Link de Pagamento</ThemedText>
                    <Button variant="outline" onPress={() => Linking.openURL(charge.paymentLink!)}>
                      <View className="flex-row items-center gap-2">
                        <Ionicons className="text-foreground" name="link" size={18} />
                        <ThemedText>Abrir Link de Pagamento</ThemedText>
                      </View>
                    </Button>
                  </View>
                )}

                {/* Dados Bancários */}
                {(charge.bankName || charge.bankAccount) && (
                  <View className="gap-2 rounded-lg bg-neutral-50 p-4">
                    <ThemedText className="mb-2 font-medium text-sm">Dados Bancários</ThemedText>
                    {charge.bankName && (
                      <View className="flex-row justify-between">
                        <ThemedText className="text-foreground/60">Banco:</ThemedText>
                        <ThemedText className="font-medium">{charge.bankName}</ThemedText>
                      </View>
                    )}
                    {charge.bankAgency && (
                      <View className="flex-row justify-between">
                        <ThemedText className="text-foreground/60">Agência:</ThemedText>
                        <ThemedText className="font-medium">{charge.bankAgency}</ThemedText>
                      </View>
                    )}
                    {charge.bankAccount && (
                      <View className="flex-row justify-between">
                        <ThemedText className="text-foreground/60">Conta:</ThemedText>
                        <ThemedText className="font-medium">{charge.bankAccount}</ThemedText>
                      </View>
                    )}
                  </View>
                )}

                {/* Instruções de Pagamento */}
                {charge.paymentInstructions && (
                  <View className="rounded-lg border border-border p-3">
                    <ThemedText className="mb-2 font-medium text-sm">Instruções</ThemedText>
                    <ThemedText className="text-sm text-foreground/80">
                      {charge.paymentInstructions}
                    </ThemedText>
                  </View>
                )}

                <ReportPaymentSection
                  onUploadPress={() => setProofModalOpen(true)}
                  onReportPress={handleReportPayment}
                />
              </View>
            </Card>
          )}

          {/* Modal de Upload */}
          {charge && (
            <ProofUploadModal
              chargeId={charge.id}
              open={proofModalOpen}
              onOpenChange={setProofModalOpen}
              onSuccess={async () => {
                const response = await api.get(`/charge/${chargeId}/public`);
                setCharge(response.data);
              }}
            />
          )}

          {/* Modal de Boas-vindas para usuários não logados */}
          {chargeId && (
            <WelcomeModal
              open={welcomeModalOpen}
              onOpenChange={setWelcomeModalOpen}
              chargeId={chargeId}
              providerName={charge?.providerName}
            />
          )}
        </View>
      </ScrollView>
    </View>
  );
}
